import type { DatePickerProps } from '@mui/x-date-pickers';
import { DatePicker } from '@mui/x-date-pickers';
import type {
  CheckboxProps,
  FormControlLabelProps,
  FormControlProps,
  SelectProps,
  TextFieldProps,
} from '@mui/material';
import type { ReactNode } from 'react';
import type { Control, FieldValues, Path } from 'react-hook-form';
import useLocalization from '../../../../hooks/useLocalization';
import { FormCheckboxField, FormPasswordField, FormSelectField, FormTextField } from '../../../molecules/form-elements';
import FormNumberInput, { type FormNumberInputProps } from '../../../molecules/form-elements/FormNumberInput';
import { mergeSx } from '../../../../utils/styles';
import { neobetFormFieldStyles as styles } from '../../../../styles/components/neobetFormFields';

export { neobetFormFieldSx } from '../../../../styles/components/neobetFormFields';

export type NeobetFormFieldProps<T extends FieldValues, TTransformedValues = T> = {
  name: Path<T>;
  control: Control<T, unknown, TTransformedValues>;
};

export const NeobetTextField = <T extends FieldValues, TTransformedValues = T>({
  sx,
  ...props
}: NeobetFormFieldProps<T, TTransformedValues> & TextFieldProps) => {
  return <FormTextField {...props} sx={mergeSx(styles.textField, sx)} />;
};

export const NeobetPasswordField = <T extends FieldValues>({
  sx,
  ...props
}: NeobetFormFieldProps<T> & Omit<TextFieldProps, 'type'>) => {
  return <FormPasswordField {...props} sx={mergeSx(styles.textField, sx)} />;
};

export const NeobetNumberField = <T extends FieldValues, TTransformedValues = T>({
  sx,
  ...props
}: FormNumberInputProps<T, TTransformedValues>) => {
  return <FormNumberInput {...props} sx={mergeSx(styles.textField, sx)} />;
};

type NeobetSelectFieldProps<T extends FieldValues> = NeobetFormFieldProps<T> & {
  options: { value: string; label: string }[];
  label?: string;
  error?: boolean;
  helperText?: string;
  FormControlProps?: FormControlProps;
  SelectProps?: Omit<SelectProps, 'label' | 'error'>;
};

export const NeobetSelectField = <T extends FieldValues>({
  FormControlProps,
  SelectProps,
  ...props
}: NeobetSelectFieldProps<T>) => {
  return (
    <FormSelectField
      {...props}
      FormControlProps={{
        ...FormControlProps,
        sx: mergeSx(styles.selectControl, FormControlProps?.sx),
      }}
      SelectProps={{
        ...SelectProps,
        variant: 'outlined',
        sx: mergeSx(styles.select, SelectProps?.sx),
        MenuProps: {
          ...SelectProps?.MenuProps,
          slotProps: {
            ...SelectProps?.MenuProps?.slotProps,
            paper: {
              sx: styles.menuPaper,
            },
          },
        },
      }}
    />
  );
};

type NeobetCheckboxFieldProps<T extends FieldValues> = NeobetFormFieldProps<T> & {
  label: string;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
  FormControlLabelProps?: Omit<FormControlLabelProps, 'control' | 'label'>;
  CheckboxProps?: Omit<CheckboxProps, 'checked' | 'disabled'>;
};

export const NeobetCheckboxField = <T extends FieldValues>({
  FormControlLabelProps,
  CheckboxProps,
  ...props
}: NeobetCheckboxFieldProps<T>) => {
  return (
    <FormCheckboxField
      {...props}
      FormControlLabelProps={{
        ...FormControlLabelProps,
        sx: mergeSx(styles.checkboxLabel, FormControlLabelProps?.sx),
      }}
      CheckboxProps={{
        ...CheckboxProps,
        sx: mergeSx(styles.checkbox, CheckboxProps?.sx),
      }}
    />
  );
};

type NeobetDateFieldProps = DatePickerProps & {
  error?: boolean;
  helperText?: ReactNode;
};

export const NeobetDateField = ({
  error,
  helperText,
  format = 'DD/MM/YYYY',
  slotProps,
  ...props
}: NeobetDateFieldProps) => {
  const { t } = useLocalization();

  return (
    <DatePicker
      desktopModeMediaQuery='@media (min-width: 0)'
      closeOnSelect
      format={format}
      slotProps={{
        ...slotProps,
        desktopPaper: {
          ...slotProps?.desktopPaper,
          sx: styles.datePickerPaper,
        },
        layout: {
          ...slotProps?.layout,
          sx: mergeSx(styles.datePickerLayout, slotProps?.layout?.sx),
        },
        popper: {
          ...slotProps?.popper,
          placement: 'bottom-end',
        },
        textField: (ownerState) => {
          const textFieldProps =
            typeof slotProps?.textField === 'function' ? slotProps.textField(ownerState) : slotProps?.textField;

          return {
            ...textFieldProps,
            error,
            helperText,
            fullWidth: true,
            sx: mergeSx(styles.textField, textFieldProps?.sx),
            slotProps: {
              ...textFieldProps?.slotProps,
              htmlInput: {
                ...textFieldProps?.slotProps?.htmlInput,
                placeholder: (props.label as string) || t('selectDate'),
              },
            },
          };
        },
      }}
      {...props}
    />
  );
};
